import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  computed
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  HowToService,
  HowToArticle,
  HowToArticlesByScope
} from 'src/app/shared/services/how-to.service';
import {
  HOW_TO_SCOPE_KEY,
  getHowToScopesFromRoute
} from 'src/app/shared/utils/how-to-route.util';
import { SharedModule } from 'src/app/shared/shared.module';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';

export { HOW_TO_SCOPE_KEY };

@Component({
  selector: 'header-how-to',
  standalone: true,
  imports: [SharedModule, NzDrawerModule, NzCollapseModule],
  templateUrl: './how-to.component.html',
  styleUrls: ['./how-to.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderHowToComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly howToService = inject(HowToService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  drawerVisible = signal(false);
  loading = signal(false);
  articleSections = signal<HowToArticlesByScope[]>([]);
  scopes = signal<string[]>([]);

  panelActive = signal<Record<string, boolean>>({});
  articleHtmlByUrl = signal<Record<string, SafeHtml | null>>({});
  articleLoadingByUrl = signal<Record<string, boolean>>({});
  articleErrorByUrl = signal<Record<string, boolean>>({});

  showButton = computed(() => this.scopes().length > 0);

  totalArticleCount = computed(() =>
    this.articleSections().reduce((n, s) => n + s.articles.length, 0)
  );

  readonly howToCollapseExpandIconPosition: 'start' | 'end' = 'start';

  ngOnInit(): void {
    this.updateScope();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.updateScope());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateScope(): void {
    if (this.drawerVisible()) {
      return;
    }
    this.scopes.set(getHowToScopesFromRoute(this.router));
  }

  openDrawer(): void {
    const scopeList = this.scopes();
    if (scopeList.length === 0) return;
    this.drawerVisible.set(true);
    this.articleSections.set([]);
    this.panelActive.set({});
    this.loading.set(true);
    this.howToService.getArticlesGroupedByScope(scopeList).subscribe({
      next: (sections) => {
        this.articleSections.set(sections);
        this.loading.set(false);
        const first = this.firstArticleInOrder(sections);
        if (first?.articleUrl) {
          this.panelActive.set({ [first.articleUrl]: true });
          this.loadArticleHtmlIfNeeded(first);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.articleSections.set([]);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  closeDrawer(): void {
    this.drawerVisible.set(false);
    this.panelActive.set({});
    this.scopes.set(getHowToScopesFromRoute(this.router));
  }

  onPanelActiveChange(a: HowToArticle, open: boolean): void {
    const u = a.articleUrl;
    if (!u) return;
    this.panelActive.update((m) => ({ ...m, [u]: open }));
    if (!open) return;
    this.loadArticleHtmlIfNeeded(a);
  }

  private firstArticleInOrder(
    sections: HowToArticlesByScope[]
  ): HowToArticle | null {
    for (const s of sections) {
      if (s.articles.length > 0) return s.articles[0];
    }
    return null;
  }

  private loadArticleHtmlIfNeeded(a: HowToArticle): void {
    const u = a.articleUrl;
    if (!u) return;
    if (this.articleHtmlByUrl()[u] != null) return;
    if (this.articleLoadingByUrl()[u]) return;

    this.articleLoadingByUrl.update((m) => ({ ...m, [u]: true }));
    this.articleErrorByUrl.update((m) => ({ ...m, [u]: false }));

    this.howToService.getArticleHtml(u).subscribe({
      next: (html) => {
        const safe = html
          ? this.sanitizer.bypassSecurityTrustHtml(html)
          : null;
        this.articleHtmlByUrl.update((m) => ({ ...m, [u]: safe }));
        this.articleLoadingByUrl.update((m) => ({ ...m, [u]: false }));
        if (!html) {
          this.articleErrorByUrl.update((m) => ({ ...m, [u]: true }));
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.articleLoadingByUrl.update((m) => ({ ...m, [u]: false }));
        this.articleErrorByUrl.update((m) => ({ ...m, [u]: true }));
        this.cdr.markForCheck();
      }
    });
  }

  isPanelActive(articleUrl: string): boolean {
    return !!this.panelActive()[articleUrl];
  }

  isArticleLoading(articleUrl: string): boolean {
    return !!this.articleLoadingByUrl()[articleUrl];
  }

  articleHtml(articleUrl: string): SafeHtml | null {
    return this.articleHtmlByUrl()[articleUrl] ?? null;
  }

  articleHasError(articleUrl: string): boolean {
    return !!this.articleErrorByUrl()[articleUrl];
  }

  openArticle(url: string): void {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  openArticleExternal(event: Event, url: string): void {
    event.stopPropagation();
    this.openArticle(url);
  }
}
